import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Persists filter URL params to sessionStorage per page per user.
 * On mount: restores saved params if URL has none of the tracked keys.
 * Call saveSnapshot(next) after applying filters.
 * Call clearSnapshot() when user explicitly clears filters.
 */
export function useFilterPersistence(
  tableKey: string,
  userId: string | undefined,
  paramKeys: string[],
  defaultFilters?: Record<string, string>,
) {
  const [searchParams, setSearchParams] = useSearchParams();
  const storageKey = `erp_filter_${userId ?? 'default'}_${tableKey}`;
  const initDone = useRef(false);

  useEffect(() => {
    if (initDone.current) return;
    initDone.current = true;
    const hasAny = paramKeys.some((k) => searchParams.get(k));
    if (hasAny) return;
    try {
      const saved = sessionStorage.getItem(storageKey);
      let entries: Record<string, string> = {};
      if (saved) {
        entries = JSON.parse(saved) as Record<string, string>;
      } else if (defaultFilters) {
        entries = defaultFilters;
      }
      const hasEntries = Object.values(entries).some(Boolean);
      if (!hasEntries) return;
      const next = new URLSearchParams(searchParams);
      Object.entries(entries).forEach(([k, v]) => {
        if (v) next.set(k, v);
        else next.delete(k);
      });
      setSearchParams(next, { replace: true });
      if (!saved && defaultFilters) {
        sessionStorage.setItem(storageKey, JSON.stringify(entries));
      }
    } catch {
      // ignore storage errors
    }
  }, []);

  /** Call with the new URLSearchParams after applying filters */
  const saveSnapshot = (next: URLSearchParams) => {
    try {
      const snapshot: Record<string, string> = {};
      paramKeys.forEach((k) => {
        const v = next.get(k);
        if (v) snapshot[k] = v;
      });
      sessionStorage.setItem(storageKey, JSON.stringify(snapshot));
    } catch {
      // ignore
    }
  };

  /** Call when user explicitly clears all filters */
  const clearSnapshot = () => {
    try {
      sessionStorage.removeItem(storageKey);
    } catch {
      // ignore
    }
  };

  return { saveSnapshot, clearSnapshot };
}